/*
 * Designer Rugs — wishlist store.

 * Swym Wishlist Plus in API/headless mode: OUR heart controls and OUR
 * wishlist page, with Swym's JS API as the backend — guest wishlists,
 * cross-device sync (by email/login) and restock/price-drop reminders
 * all ride Swym once the app is installed and its
 * app embed is enabled. Until `window._swat` exists this module runs on
 * localStorage with identical behaviour (device-local only), and the
 * moment Swym arrives any locally saved items migrate up once.

 * Swym-side flags (configure in the Swym admin, not code):
 * - Enable API/custom mode so Swym injects NO default UI.
 * - Reminders (restock / price drop) are Swym marketing automations on
 * wishlisted items — switched on there, nothing extra in the theme.

 * Heart contract: any element with [data-wishlist-toggle] plus
 * data-handle / data-product-id / data-variant-id / data-url.
 * State flows one way: store -> `wishlist:change` on document ->
 * every heart re-renders (aria-pressed drives the filled treatment).
 */
(() => {
  if (window.DRWishlist) return;

  const LS_KEY = 'dr:wishlist:v1';
  const items = new Map(); // handle -> { handle, empi, epi, du }
  let swym = null;

  /* ---------------- persistence providers ----------------*/
  function readLocal() {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY)) || [];
    } catch (e) {
      return [];
    }
  }
  function writeLocal() {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(Array.from(items.values())));
    } catch (e) {
      /* Private mode — session-only wishlist*/
    }
  }

  function handleFromDu(du) {
    try {
      const m = new URL(du, window.location.origin).pathname.match(/\/products\/([^/?]+)/);
      return m ? m[1] : null;
    } catch (e) {
      return null;
    }
  }

  function announce() {
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: Array.from(items.keys()) } }));
  }

  function loadFromSwym() {
    swym.fetch((products) => {
      items.clear();
      (products || []).forEach((p) => {
        const handle = handleFromDu(p.du);
        if (handle) items.set(handle, { handle, empi: p.empi, epi: p.epi, du: p.du });
      });
      announce();
    });
  }

  /* ---------------- public store ----------------*/
  const store = {
    has(handle) {
      return items.has(handle);
    },
    list() {
      return Array.from(items.values());
    },
    add(item) {
      items.set(item.handle, item);
      announce();
      if (swym) {
        swym.addToWishList({ empi: item.empi, epi: item.epi, du: item.du }, () => loadFromSwym());
      } else {
        writeLocal();
      }
    },
    remove(handle) {
      const item = items.get(handle);
      if (!item) return;
      items.delete(handle);
      announce();
      if (swym) {
        swym.removeFromWishList({ empi: item.empi, epi: item.epi, du: item.du }, () => loadFromSwym());
      } else {
        writeLocal();
      }
    },
    toggle(item) {
      if (items.has(item.handle)) this.remove(item.handle);
      else this.add(item);
    },
  };
  window.DRWishlist = store;

  /* ---------------- boot: local first, Swym when it lands ------------*/
  readLocal().forEach((it) => {
    if (it.handle) items.set(it.handle, it);
  });
  announce();

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push((tracker) => {
    swym = tracker;
    // one-way migration of anything saved before the app was installed
    const pending = readLocal();
    try {
      localStorage.removeItem(LS_KEY);
    } catch (e) { /* Fine*/ }
    if (pending.length) {
      pending.forEach((it) => swym.addToWishList({ empi: it.empi, epi: it.epi, du: it.du }));
      // Swym batches internally; re-read shortly after
      setTimeout(() => loadFromSwym(), 1500);
    }
    loadFromSwym();
  });

  /* ---------------- heart controls ----------------*/
  function itemFromToggle(btn) {
    return {
      handle: btn.dataset.handle,
      empi: Number(btn.dataset.productId),
      epi: Number(btn.dataset.variantId),
      du: new URL(btn.dataset.url || `/products/${btn.dataset.handle}`, window.location.origin).href,
    };
  }

  function paintToggles() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const saved = store.has(btn.dataset.handle);
      const pressed = String(saved);
      if (btn.getAttribute('aria-pressed') !== pressed) btn.setAttribute('aria-pressed', pressed);
      if (btn.dataset.labelAdd) {
        const label = saved ? btn.dataset.labelRemove : btn.dataset.labelAdd;
        // only write on change — the mutation observer below watches
        // childList and an unconditional write would loop it
        if (btn.textContent !== label) btn.textContent = label;
      }
    });
  }

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-wishlist-toggle]');
    if (!btn || !btn.dataset.handle) return;
    event.preventDefault();
    store.toggle(itemFromToggle(btn));
    // User action only: hydration, repaint and Swym sync are not conversions.
    if (typeof window.drTrack === 'function') {
      window.drTrack('dr_wishlist', {
        action: store.has(btn.dataset.handle) ? 'add' : 'remove',
        product_handle: btn.dataset.handle,
        product_id: btn.dataset.productId || null,
        variant_id: btn.dataset.variantId || null,
      });
    }
  });

  document.addEventListener('wishlist:change', paintToggles);
  // late-rendered hearts (child swap, infinite scroll, section reloads)
  document.addEventListener('shopify:section:load', paintToggles);
  let paintTimer = null;
  new MutationObserver(() => {
    clearTimeout(paintTimer);
    paintTimer = setTimeout(paintToggles, 120);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintToggles, { once: true });
  } else {
    paintToggles();
  }
})();
